import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GroupBase, OptionsOrGroups } from "react-select";
import { toast } from "react-toastify";
import {
  useEditOrganizationVehicleMutation,
  useLazyOrganizationVehiclesQuery,
  useOrganizationVehiclesQuery,
} from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import { APP_CONFIG } from "../../../constants/constants";
import { asyncDebounce } from "../../../utils/common";
import { useLoggedInUserData } from "../../../utils/user";
import { ACCOUNT_RECORDER_TYPE_VALUES } from "../../admin-details/account/constants";

type ModalLinkAssetProps = {
  cameraId: string;
  assetId: string;
  onSuccess: () => void;
};

const ModalLinkAsset: FC<ModalLinkAssetProps> = ({
  cameraId,
  assetId,
  onSuccess,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });
  const { t: tVehicle } = useTranslation("translation", {
    keyPrefix: "admins.vehicles.detailsPage",
  });
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const [orgVehiclesQuery, { isFetching: isFetchingOrgVehicles }] =
    useLazyOrganizationVehiclesQuery();
  const [vehileDropdownSearchResults, setVehicleDropdownSearchResults] =
    useState<TLegacyVehicle[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<
    string | undefined
  >();
  const [hasAlreadyAssetId, setHasAlreadyAssetId] = useState<boolean>(
    assetId ? true : false
  );
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");

  // vehicle mutations
  const [
    editOrganizationVehicleApiTrigger,
    { isLoading: isLoadingEditVehicle },
  ] = useEditOrganizationVehicleMutation();
  // fetching devices list
  const {
    data: dataInitialDevicesList,
    isFetching: isFetchingInitialDevicesList,
  } = useOrganizationVehiclesQuery({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
    search: "",
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
    isactive: 1,
  });

  const loadOptionsHandlerVehicle = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: vehiclesQueryResponse } = await orgVehiclesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page || 2,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
      isactive: 1,
    });

    if (!vehiclesQueryResponse || !vehiclesQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: additional?.page || 2,
        },
      };
    }
    const { results } = vehiclesQueryResponse as {
      results: TLegacyVehicle[];
    };
    setVehicleDropdownSearchResults(results);
    return {
      options: !!results
        ? (results || ([] as TLegacyVehicle[])).map(
            (item: TLegacyVehicle, index: number) =>
              ({
                value: `${item.deviceid}`,
                label: item.deviceid,
              }) as TSelectboxOption
          )
        : [],
      hasMore: vehiclesQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };

  useEffect(() => {
    if (modalsState.showModalLinkAsset) {
      if (assetId) {
        setHasAlreadyAssetId(true);
        setSelectedDeviceId(undefined);
      } else {
        setHasAlreadyAssetId(false);
        setSelectedDeviceId(undefined);
      }
    }
  }, [modalsState.showModalLinkAsset, assetId]);

  const debouncedLoadOptionsHandlerVehicle = asyncDebounce(
    loadOptionsHandlerVehicle,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showModalLinkAsset: false }));
  };

  const handleSubmit = () => {
    // prepare payload
    const preparedVehiclePayload: Partial<TLegacyVehicle> = {
      deviceid: selectedDeviceId,
      cameraid: cameraId,
      accountid: `${thisUserOrganizationId}`,
      recordertype: parseInt(ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX),
    };
    // call api for updating vehicle
    editOrganizationVehicleApiTrigger(preparedVehiclePayload)
      .unwrap()
      .then(() => {
        toast.success(tVehicle("toast.vehicle_updated"));
        hideModal();
        onSuccess();
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [tVehicle("toast.updation_failed")];
        toast.error(errors?.join(" "));
      });
  };

  if (modalsState.showModalLinkAsset === false) return null;

  return (
    <>
      <div
        className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
        onClick={hideModal}
      ></div>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none z-modal">
        <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[480px] z-modal">
          <form
            className="p-8 bg-white grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
            onSubmit={handleSubmit}
          >
            <h2 className="col-span-6 text-3xl font-bold text-heading-black">
              {t("link_asset")}
            </h2>
            <div className="col-span-6 mt-0 grid grid-cols-6">
              <p className="col-span-6 font-semibold text-base leading-6 align-middle text-gray-900">
                {t("camera_id")}
              </p>
              <p className="col-span-6 mt-0 font-semibold text-base leading-6 align-middle text-accent-blue-bright">
                {cameraId}
              </p>
            </div>
            <div className="col-span-6">
              <p className="w-full font-semibold text-base leading-6 align-middle text-gray-900">
                {t("search_asset_to_link")}
              </p>
              {/* Device ID Selector */}
              {hasAlreadyAssetId ? (
                <div className="col-span-6 grid grid-cols-3 gap-2">
                  <AdminFormFieldInput
                    type="text"
                    label={""}
                    id={"assetid"}
                    name={"assetid"}
                    placeholder={t("search_placeholder")}
                    value={assetId}
                    disabled={true}
                    customWrapperClass="col-span-2"
                  />
                  <AdminFormFieldSubmit
                    label={tMain("change")}
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setHasAlreadyAssetId(false);
                      setSelectedDeviceId(undefined);
                    }}
                    customWrapperClass="col-span-1 mt-1"
                    buttonDimensionClass="w-full h-11 px-4"
                  />
                </div>
              ) : (
                <AdminFormFieldAsyncLazyDropdown
                  key={"asset_field"}
                  customWrapperClass="col-span-6"
                  loadingData={
                    isFetchingInitialDevicesList || isFetchingOrgVehicles
                  }
                  label={""}
                  id={"assetid"}
                  name={"assetid"}
                  placeholder={t("search_placeholder")}
                  isMulti={false}
                  additional={{ page: 1 }}
                  loadOptions={debouncedLoadOptionsHandlerVehicle}
                  onChange={(e) => {
                    setSelectedDeviceId(e?.value);
                  }}
                  value={selectedDeviceId}
                  detailsFormField={true}
                />
              )}
            </div>
            <AdminFormFieldSubmit
              label={tMain("confirm")}
              type="button"
              variant="primary"
              disabled={!selectedDeviceId || isLoadingEditVehicle}
              onClick={handleSubmit}
            />

            <AdminFormFieldSubmit
              label={tMain("cancel")}
              type="button"
              variant="danger-transparent"
              onClick={hideModal}
            />
          </form>
        </div>
      </div>
    </>
  );
};

export default ModalLinkAsset;
